import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, CheckCircle, Phone, Settings, Shield, Filter, RotateCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const SuctionScreensPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Suction Screens — The First Line of Defense for Your Pump | CTC Waterworks"
        description="Protect pumps, filters, and emitters with rugged, self-cleaning suction screens built for agricultural and industrial water systems. Standard, Self-Cleaning (SCS2), and Passive Fish (PSF) models."
        canonicalPath="/filtration/suction-screens"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Suction Screens</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Suction Screens — The First Line of Defense for Your Pump
            </h1>
            <p className="text-2xl text-gray-600 mb-8 font-light">
              Protect pumps, filters, and emitters with rugged, self-cleaning suction screens built for agricultural and industrial water systems.
            </p>

            <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg mb-8">
              <p className="text-gray-800 leading-relaxed text-lg">
                CTC Suction Screens provide reliable pump protection and pre-filtration for irrigation, municipal, and industrial systems. Built for high flow and harsh conditions, these screens prevent debris from entering pumps or emitters while maintaining consistent flow. Choose from standard, self-cleaning (SCS2), or passive fish-safe models (PSF) to match your application and regulatory requirements.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Family Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Choose Your Suction Screen Solution</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Standard Suction Screen Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-lg shadow-xl overflow-hidden border-2 border-gray-300 hover:border-gray-700 hover:shadow-2xl transition-all flex flex-col"
            >
              <div className="bg-gradient-to-r from-gray-600 to-gray-700 p-6">
                <Filter className="text-white mb-3" size={40} />
                <h3 className="text-xl font-bold text-white">Standard Suction Screen</h3>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Robust intake protection with simple, fixed design requiring no moving parts or power.
                </p>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="text-gray-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Manual cleaning (hose flush or brush)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-gray-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Flow: 100–8,500+ GPM</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-gray-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Carbon steel, aluminum, or stainless steel</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-gray-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Ideal for canals, ponds, or tanks</span>
                  </li>
                </ul>

                <div className="mt-auto">
                  <Link
                    to="/filtration/suction-screens/standard"
                    className="block w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-gray-700 hover:to-gray-800 transition-all shadow-lg text-center"
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* SCS2 Self-Cleaning Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-lg shadow-xl overflow-hidden border-2 border-ctc-blue hover:shadow-2xl transition-all flex flex-col"
            >
              <div className="bg-gradient-to-r from-ctc-blue to-ctc-blue-dark p-6">
                <RotateCw className="text-white mb-3" size={40} />
                <h3 className="text-xl font-bold text-white">SCS2 Self-Cleaning Screen</h3>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Automatic cleaning powered by pump discharge. No external power required.
                </p>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="text-ctc-blue mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Spray-bar driven by pump discharge</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-ctc-blue mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Flow: 100–8,500+ GPM</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-ctc-blue mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Carbon steel, aluminum, or stainless steel</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-ctc-blue mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Ideal for canals, ponds, or tanks</span>
                  </li>
                </ul>

                <div className="mt-auto">
                  <Link
                    to="/filtration/suction-screens/scs2"
                    className="block w-full bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-6 py-3 rounded-lg font-semibold hover:from-ctc-blue-dark hover:to-ctc-blue transition-all shadow-lg text-center"
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* PSF Passive Fish Screen Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-lg shadow-xl overflow-hidden border-2 border-gray-300 hover:border-green-600 hover:shadow-2xl transition-all flex flex-col"
            >
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-6">
                <Shield className="text-white mb-3" size={40} />
                <h3 className="text-xl font-bold text-white">PSF Passive Fish Screen</h3>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Environmentally compliant screening for fish-protection regulations.
                </p>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="text-green-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Meets approach velocity limits</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Flow: 100–3,000 GPM</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Wedgewire stainless steel</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-600 mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Environmental compliance</span>
                  </li>
                </ul>

                <div className="mt-auto">
                  <Link
                    to="/filtration/suction-screens/passive-fish-screens"
                    className="block w-full bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all shadow-lg text-center"
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-800 font-medium">
              All models feature heavy-duty mesh or wedgewire screen cylinders and are sized for optimal approach velocity and minimal head loss.
            </p>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Key Features</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Performance */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Performance</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-ctc-blue mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700">Prevents clogging of emitters and filters</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-ctc-blue mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700">Reduces pump wear and cavitation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-ctc-blue mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700">Maintains steady system pressure</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-ctc-blue mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700">Compatible with all irrigation and pumping systems</span>
                </li>
              </ul>
            </div>

            {/* Right Column - Design */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Design</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700">Flow-through design with 60–70% open area</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700">Screen cylinders in 304 or 316 stainless steel</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700">Replaceable end bells and fittings</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700">Available in flanged, threaded, or slip connections</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700">Rated up to 150 PSI</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>


      {/* Screen Options */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Common Screen Options</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Below are commonly specified mesh sizes and screen types. Custom mesh, perforation sizes, and wedgewire slot widths are available to meet your specific requirements.
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full bg-white shadow-lg rounded-lg overflow-hidden">
              <thead className="bg-ctc-blue text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Mesh</th>
                  <th className="px-6 py-4 text-left font-semibold">Micron</th>
                  <th className="px-6 py-4 text-left font-semibold">Screen Type</th>
                  <th className="px-6 py-4 text-left font-semibold">Typical Use</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">20</td>
                  <td className="px-6 py-4 text-gray-700">841 µm</td>
                  <td className="px-6 py-4 text-gray-700">Perforated plate</td>
                  <td className="px-6 py-4 text-gray-700">Large debris, intake protection</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">40</td>
                  <td className="px-6 py-4 text-gray-700">420 µm</td>
                  <td className="px-6 py-4 text-gray-700">Mesh</td>
                  <td className="px-6 py-4 text-gray-700">General irrigation</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">60</td>
                  <td className="px-6 py-4 text-gray-700">250 µm</td>
                  <td className="px-6 py-4 text-gray-700">Mesh</td>
                  <td className="px-6 py-4 text-gray-700">Fine irrigation emitters</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">80</td>
                  <td className="px-6 py-4 text-gray-700">177 µm</td>
                  <td className="px-6 py-4 text-gray-700">Mesh</td>
                  <td className="px-6 py-4 text-gray-700">Fertigation / micro irrigation</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Wedgewire</td>
                  <td className="px-6 py-4 text-gray-700">0.06–0.125″ slot</td>
                  <td className="px-6 py-4 text-gray-700">Fish-safe</td>
                  <td className="px-6 py-4 text-gray-700">Environmental compliance</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Fish-Safe Screen Note</h3>
            <p className="text-gray-700">
              Wedgewire (PSF) screens maintain &lt;0.2 ft/sec approach velocity for regulatory fish-safe operation.
            </p>
          </div>
        </div>
      </section>

      {/* Standard Suction Screens Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Standard Suction Screens</h2>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Standard Suction Screens provide robust intake protection with a simple, fixed design requiring no moving parts or power. Ideal for low-flow applications, clean water sources, or installations where manual maintenance is acceptable. Simply lift the screen from the water, hose off debris, and reinstall—typical service time 15-30 minutes.
            </p>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <li className="flex items-start">
                <Filter className="text-gray-700 mr-3 mt-1 flex-shrink-0" size={24} />
                <span className="text-gray-700">No moving parts, no power required</span>
              </li>
              <li className="flex items-start">
                <Filter className="text-gray-700 mr-3 mt-1 flex-shrink-0" size={24} />
                <span className="text-gray-700">Replaceable screen elements for extended service life</span>
              </li>
              <li className="flex items-start">
                <Filter className="text-gray-700 mr-3 mt-1 flex-shrink-0" size={24} />
                <span className="text-gray-700">Available in epoxy-coated steel or stainless steel</span>
              </li>
              <li className="flex items-start">
                <Filter className="text-gray-700 mr-3 mt-1 flex-shrink-0" size={24} />
                <span className="text-gray-700">Flow range: 150-2500 GPM</span>
              </li>
            </ul>
          </div>

          <div className="text-center">
            <Link
              to="/filtration/suction-screens/standard"
              className="inline-flex items-center bg-gray-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-lg"
            >
              <span>View Standard Suction Screen Specifications</span>
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* How Self-Cleaning Works (SCS2) */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">How Self-Cleaning Works (SCS2)</h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                The SCS2 Self-Cleaning Suction Screen uses pump discharge water to power an internal rotating spray bar. The spray bar rotates inside the stationary screen cylinder, dislodging debris while maintaining full flow to the pump. The screen itself does not rotate—there are no external moving parts. The system requires no external power, valves, or controls — cleaning happens automatically whenever the pump operates.
              </p>

              <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500">
                <Settings className="mx-auto mb-4" size={48} />
                <p className="italic">Insert diagram showing spray-bar mechanism</p>
              </div>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Key Advantages</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-ctc-blue font-bold mr-3">•</span>
                  <span className="text-gray-700">Powered by pump discharge (no electricity)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue font-bold mr-3">•</span>
                  <span className="text-gray-700">Fully automatic cleaning</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue font-bold mr-3">•</span>
                  <span className="text-gray-700">Minimal backpressure</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue font-bold mr-3">•</span>
                  <span className="text-gray-700">No moving parts exposed to debris</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue font-bold mr-3">•</span>
                  <span className="text-gray-700">Proven field reliability in canal and surface water systems</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/filtration/suction-screens/self-cleaning"
              className="inline-flex items-center bg-ctc-blue text-white px-8 py-4 rounded-lg font-semibold hover:bg-ctc-blue-dark transition-colors shadow-lg"
            >
              <span>Learn More About SCS2 Self-Cleaning</span>
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Passive Fish Screen (PSF) Overview */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Passive Fish Screen (PSF) Overview</h2>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              The PSF Series provides environmentally compliant screening for installations subject to fish-protection regulations. Precision-engineered wedgewire cylinders maintain safe approach velocities while ensuring high flow efficiency and long service intervals.
            </p>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <li className="flex items-start">
                <Shield className="text-green-600 mr-3 mt-1 flex-shrink-0" size={24} />
                <span className="text-gray-700">Meets NMFS and WDFW guidelines for fish protection</span>
              </li>
              <li className="flex items-start">
                <Shield className="text-green-600 mr-3 mt-1 flex-shrink-0" size={24} />
                <span className="text-gray-700">304 or 316 stainless wedgewire with reinforced frame</span>
              </li>
              <li className="flex items-start">
                <Shield className="text-green-600 mr-3 mt-1 flex-shrink-0" size={24} />
                <span className="text-gray-700">Custom lengths and diameters to match flow requirements</span>
              </li>
              <li className="flex items-start">
                <Shield className="text-green-600 mr-3 mt-1 flex-shrink-0" size={24} />
                <span className="text-gray-700">Optional air burst cleaning system available</span>
              </li>
              <li className="flex items-start">
                <Shield className="text-green-600 mr-3 mt-1 flex-shrink-0" size={24} />
                <span className="text-gray-700">Compatible with gravity or pumped intakes</span>
              </li>
            </ul>
          </div>

          <div className="text-center">
            <Link
              to="/filtration/suction-screens/passive-fish-screens"
              className="inline-flex items-center bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg"
            >
              <span>Learn More About PSF Passive Fish Screens</span>
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Materials & Construction */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Materials & Construction</h2>

          <div className="bg-gray-50 rounded-lg shadow-lg p-8">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <li className="flex items-start">
                <CheckCircle className="text-ctc-blue mr-3 mt-1 flex-shrink-0" size={20} />
                <span className="text-gray-700">304 or 316 stainless steel screen cylinders</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="text-ctc-blue mr-3 mt-1 flex-shrink-0" size={20} />
                <span className="text-gray-700">End bells and flanges in carbon or stainless steel</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="text-ctc-blue mr-3 mt-1 flex-shrink-0" size={20} />
                <span className="text-gray-700">Pressure rating: 150 PSI</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="text-ctc-blue mr-3 mt-1 flex-shrink-0" size={20} />
                <span className="text-gray-700">Standard EPDM seals</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="text-ctc-blue mr-3 mt-1 flex-shrink-0" size={20} />
                <span className="text-gray-700">Optional cathodic protection or epoxy coating</span>
              </li>
            </ul>
          </div>
        </div>
      </section>


      {/* Cross-sell Card - Pair with BIG Inline Filters */}
      <section className="py-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <Link
            to="/filtration/inline/big"
            className="block bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-all border-2 border-transparent hover:border-ctc-blue group"
          >
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <Settings className="text-ctc-blue group-hover:scale-110 transition-transform" size={48} />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-ctc-blue transition-colors">
                  Pair with BIG Inline Filters
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Complete your filtration system with CTC BIG Inline Filters. Available with manual, brush, or vacuum cleaning options.
                </p>
                <span className="inline-flex items-center text-ctc-blue font-semibold">
                  View BIG Series <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Downloadable Resources */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Downloadable Resources</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href="#"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border border-gray-200 hover:border-ctc-blue group"
            >
              <Download className="text-ctc-blue mb-4 group-hover:scale-110 transition-transform" size={32} />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Suction Screens Spec Sheet</h3>
              <p className="text-gray-600 text-sm mb-4">Complete specifications for all suction screen models</p>
              <span className="text-ctc-blue font-semibold flex items-center">
                Download PDF <ArrowRight size={16} className="ml-2" />
              </span>
            </a>

            <a
              href="#"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border border-gray-200 hover:border-ctc-blue group"
            >
              <Download className="text-ctc-blue mb-4 group-hover:scale-110 transition-transform" size={32} />
              <h3 className="text-lg font-bold text-gray-900 mb-2">2021 CTC Catalog</h3>
              <p className="text-gray-600 text-sm mb-4">Full filtration section with technical details</p>
              <span className="text-ctc-blue font-semibold flex items-center">
                Download PDF <ArrowRight size={16} className="ml-2" />
              </span>
            </a>

            <a
              href="#"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border border-gray-200 hover:border-ctc-blue group"
            >
              <Download className="text-ctc-blue mb-4 group-hover:scale-110 transition-transform" size={32} />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Spray Nozzle and Screen Parts Guide</h3>
              <p className="text-gray-600 text-sm mb-4">Replacement parts and maintenance components</p>
              <span className="text-ctc-blue font-semibold flex items-center">
                Download PDF <ArrowRight size={16} className="ml-2" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Protect your pump with proven CTC suction screens.</h2>
          <p className="text-xl text-white/90 mb-8">
            From standard manual screens to fully automatic self-cleaning systems, we have the right solution for your intake protection needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/filtration/sizing"
              className="inline-flex items-center justify-center bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              <span>Size My Intake Screen</span>
              <ArrowRight size={20} className="ml-2" />
            </Link>
            <a
              href="tel:+15099017476"
              className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-ctc-blue transition-colors"
            >
              <Phone size={20} className="mr-2" />
              <span>(509) 901-7476</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SuctionScreensPage;
